import { css } from 'astroturf';
import fetchJsonp from 'fetch-jsonp';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import PageHeader from '../../components/PageHeader';
import SEO from '../../components/SEO';
import RequestQuote from '../../components/pricetable/RequestQuote';
import Layout from '../../layout';

const PADDLE_VENDOR = 39438;

/**
 * Plans that can be quoted. The `ids` are the same Paddle product ids the
 * pricing table uses (src/components/pricetable/Table.jsx) so a quote always
 * reflects whatever Paddle currently charges, including the visitor's local
 * currency and VAT.
 *
 * Seats are fixed for now: Individual is a single seat, Business is the
 * standard 10 seat plan. When per-seat quoting lands, `seats` becomes a value
 * the visitor picks, starting at `seats` and going up in steps of `seatStep`.
 * Everything downstream already multiplies by `units`, so only the `units`
 * calculation below has to change then.
 */
const PLANS = {
  individual: {
    key: 'individual',
    name: 'Individual',
    seats: 1,
    seatStep: null,
    ids: { yearly: 599947, monthly: 599945 },
  },
  business: {
    key: 'business',
    name: 'Business',
    seats: 10,
    seatStep: 5,
    ids: { yearly: 599949, monthly: 599948 },
  },
};

const CURRENCY_NAMES = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'Pound Sterling',
  ARS: 'Argentine Peso',
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  CHF: 'Swiss Franc',
  CZK: 'Czech Koruna',
  DKK: 'Danish Krone',
  HKD: 'Hong Kong Dollar',
  HUF: 'Hungarian Forint',
  INR: 'Indian Rupee',
  ILS: 'Israeli Shekel',
  JPY: 'Japanese Yen',
  KRW: 'South Korean Won',
  MXN: 'Mexican Peso',
  NOK: 'Norwegian Krone',
  NZD: 'New Zealand Dollar',
  PLN: 'Polish Zloty',
  RUB: 'Russian Ruble',
  SEK: 'Swedish Krona',
  SGD: 'Singapore Dollar',
  THB: 'Thai Baht',
  TRY: 'Turkish Lira',
  TWD: 'New Taiwan Dollar',
  UAH: 'Ukrainian Hryvnia',
};

const QUOTE_ID = 'quote-document';
const HIDDEN_CLASS = 'quote-print-hidden';

/**
 * Printing shows the quote and nothing else. Rather than guessing at the page
 * structure in CSS, every sibling along the path from the quote up to <body>
 * is marked right before the print snapshot is taken and unmarked afterwards.
 * Using beforeprint/afterprint means Ctrl+P works the same as the button.
 *
 * Inside the quote the editing affordances are marked `quote-ui` so they drop
 * out, and the selects and inline edit buttons are flattened back to plain
 * text so the printed quote reads as a document rather than a form.
 */
const printStyles = `
@media print {
  html, body { background: #fff !important; }

  .${HIDDEN_CLASS}, .quote-ui { display: none !important; }

  #${QUOTE_ID} {
    margin: 0 !important;
    padding: 0 !important;
    max-width: none !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: #fff !important;
    font-size: 10pt;
  }

  #${QUOTE_ID} .quote-print-only { display: inline !important; }

  #${QUOTE_ID} button {
    -webkit-appearance: none !important;
    appearance: none !important;
    font: inherit !important;
    color: inherit !important;
    background: none !important;
    border: 0 !important;
    border-radius: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    text-align: inherit !important;
  }

  #${QUOTE_ID} .quote-recipient { min-height: 0 !important; }
  #${QUOTE_ID} .quote-signature-off { display: none !important; }

  /* Tightened up so a single-line quote fits on one sheet. */
  #${QUOTE_ID} .quote-parties { padding: 0.75rem 0 !important; }
  #${QUOTE_ID} .quote-terms { margin-top: 1rem !important; }
  #${QUOTE_ID} .quote-signature { margin-top: 1.25rem !important; }
  #${QUOTE_ID} .quote-disclaimer { margin-top: 1.25rem !important; }
  #${QUOTE_ID} table th,
  #${QUOTE_ID} table td { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
  #${QUOTE_ID} .quote-signature svg { width: 10rem !important; }
}

@page {
  margin: 15mm;
}
`;

/**
 * Paddle hands back pre-formatted price strings ("$249.00", "1.234,56 €").
 * Turn those back into a number so amounts can be multiplied and re-formatted.
 */
const parseAmount = (input) => {
  if (input === undefined || input === null) return NaN;

  const cleaned = String(input).replace(/[^0-9.,-]/g, '');
  if (!cleaned) return NaN;

  const lastSeparator = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
  if (lastSeparator === -1) return parseFloat(cleaned);

  const decimals = cleaned.length - lastSeparator - 1;
  if (decimals !== 1 && decimals !== 2) {
    // Not a decimal separator, so it groups thousands.
    return parseFloat(cleaned.replace(/[.,]/g, ''));
  }

  const whole = cleaned.slice(0, lastSeparator).replace(/[.,]/g, '');
  return parseFloat(`${whole}.${cleaned.slice(lastSeparator + 1)}`);
};

const formatMoney = (value, currency) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  if (!currency) return value.toFixed(2);

  let formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
  }).format(value);

  if (formatted.startsWith('US$')) formatted = formatted.replace('US$', '$');

  return formatted;
};

const formatDate = (date) => date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

// Focus an inline editor the moment it appears, without the autoFocus prop.
const focusOnMount = (node) => {
  if (node && document.activeElement !== node) node.focus();
};

const styles = css`
  .intro {
    max-width: 46rem;
    margin: 0 auto;
  }

  .wrap {
    max-width: 60rem;
    margin: 0 auto 4rem;
    padding: 0 0.25rem;

    @media (min-width: 40rem) {
      padding: 0 2rem;
    }
  }

  .toolbar {
    display: flex;
    justify-content: flex-end;
    margin: 0 0 1rem;
  }

  .printbutton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    height: 2.75rem;
    border: 0;
    border-radius: 10px;
    padding: 0 1.25rem;
    background: #107db5;
    font-weight: 600;
    font-size: 1rem;
    color: #fff;
    cursor: pointer;
    transition: 0.25s ease-in-out background;

    &:hocus {
      background: #67b26f;
    }
  }

  .buttonicon {
    flex: none;
    width: 1.25rem;
    height: 1.25rem;
  }

  .quote {
    background: #fff;
    color: #1a202c;
    border: 1px solid rgba(0, 70, 150, 0.2);
    border-radius: 0.5rem;
    box-shadow:
      0 1px 10px rgba(0, 0, 0, 0.05),
      0 5px 10px rgba(0, 0, 0, 0.06);
    padding: 2rem;
    font-size: 0.9375rem;
    line-height: 1.6;

    @media (min-width: 40rem) {
      padding: 3rem;
    }
  }

  /* Inline editing affordances */
  .select {
    appearance: none;
    font: inherit;
    font-weight: 600;
    color: #107db5;
    background-color: rgba(16, 125, 181, 0.09);
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23107db5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.4rem center;
    background-size: 0.5rem;
    border: 0;
    border-radius: 0.25rem;
    padding: 0.0625rem 1.25rem 0.0625rem 0.375rem;
    cursor: pointer;
    transition: 0.2s ease-in-out background-color;

    &:hocus {
      background-color: rgba(16, 125, 181, 0.2);
    }
  }

  .addbutton {
    font: inherit;
    font-size: 0.8125rem;
    line-height: 1.4;
    color: #107db5;
    background: none;
    border: 1px dashed rgba(16, 125, 181, 0.5);
    border-radius: 0.25rem;
    padding: 0.125rem 0.5rem;
    cursor: pointer;
    transition:
      0.2s ease-in-out background,
      0.2s ease-in-out border-color;

    &:hocus {
      background: rgba(16, 125, 181, 0.1);
      border-style: solid;
    }
  }

  .editable {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    padding: 0;
    margin: 0;
    text-align: inherit;
    cursor: text;
    border-radius: 0.125rem;

    &:hocus {
      background: rgba(16, 125, 181, 0.12);
      box-shadow: 0 0 0 3px rgba(16, 125, 181, 0.12);
    }
  }

  /* Same dashed box as the recipient button. It is taller than a line of the
     meta list, so the cell it sits in is locked to one line and the button is
     allowed to overflow it: the row keeps its height whichever state it is in. */
  .addreference {
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.3;
    white-space: nowrap;
    color: #107db5;
    background: none;
    border: 1px dashed rgba(16, 125, 181, 0.5);
    border-radius: 0.25rem;
    padding: 0.125rem 0.5rem;
    cursor: pointer;
    transition:
      0.2s ease-in-out background,
      0.2s ease-in-out border-color;

    &:hocus {
      background: rgba(16, 125, 181, 0.1);
      border-style: solid;
    }
  }

  /* Every state of the recipient (add button, textarea, committed text) sits
     in a slot of the same height, so opening the editor moves nothing. */
  .recipientslot {
    min-height: 6.5rem;
  }

  .recipienttext {
    display: block;
    white-space: pre-line;
    width: 100%;
  }

  .recipientinput {
    display: block;
    font: inherit;
    width: 100%;
    height: 6.5rem;
    border: 1px solid rgba(16, 125, 181, 0.5);
    border-radius: 0.25rem;
    padding: 0.5rem 0.625rem;
    background: rgba(16, 125, 181, 0.05);
    box-sizing: border-box;
    resize: vertical;
  }

  /* Fixed width and no border, so swapping between the button, the input and
     the value never changes the size of the meta block. */
  .metarow {
    display: contents;
  }

  .referencevalue {
    width: 10rem;
    max-width: 100%;
    height: 1.3rem;
    height: 1lh;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .referenceinput {
    font: inherit;
    font-weight: 600;
    text-align: right;
    width: 100%;
    border: 0;
    padding: 0;
    background: rgba(16, 125, 181, 0.06);
    box-shadow: 0 1px 0 rgba(16, 125, 181, 0.6);
  }

  .quoteheader {
    display: flex;
    flex-flow: row wrap;
    gap: 1.5rem;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 2px solid #107db5;
    padding-bottom: 1.5rem;
  }

  .logo {
    display: block;
    width: auto;
    height: 2.25rem;
  }

  .quotemeta {
    text-align: right;
    margin-left: auto;

    & h2 {
      margin: 0 0 0.25rem;
      font-size: 1.5rem;
      text-transform: uppercase;
      color: #107db5;
    }

    & dl {
      margin: 0;
      display: grid;
      grid-template-columns: auto auto;
      gap: 0 0.75rem;
      font-size: 0.8125rem;
      justify-content: end;
      align-items: baseline;
    }

    & dt {
      color: #4a5568;
      margin: 0;
    }

    & dd {
      margin: 0;
      font-weight: 600;
      text-align: right;
    }
  }

  .parties {
    display: grid;
    gap: 2rem;
    padding: 1.5rem 0;

    @media (min-width: 40rem) {
      grid-template-columns: 1fr 1fr;
    }

    & h3 {
      font-size: 0.8125rem;
      color: #4a5568;
      margin: 0 0 0.5rem;
      font-weight: 700;
    }

    & p {
      margin: 0;
      white-space: pre-line;
    }
  }

  .table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0 0;

    & th,
    & td {
      text-align: left;
      padding: 0.75rem 0.5rem;
      vertical-align: top;
    }

    & th:last-child,
    & td:last-child {
      text-align: right;
      white-space: nowrap;
    }

    & thead th {
      font-size: 0.8125rem;
      color: #4a5568;
      border-bottom: 1px solid rgba(0, 70, 150, 0.25);
      font-weight: 700;
    }

    & tbody td {
      border-bottom: 1px solid rgba(0, 70, 150, 0.12);
    }

    & tbody small {
      display: block;
      color: #4a5568;
      font-size: 0.8125rem;
      margin-top: 0.25rem;
    }

    & tfoot th {
      font-weight: 400;
      text-align: right;
      color: #4a5568;
    }

    & tfoot td {
      font-variant-numeric: tabular-nums;
    }

    & tfoot tr:last-child th,
    & tfoot tr:last-child td {
      font-size: 1.125rem;
      font-weight: 700;
      color: #0a1530;
      border-top: 2px solid #107db5;
      padding-top: 0.75rem;
    }
  }

  .terms {
    margin: 2rem 0 0;
    display: grid;
    gap: 0.5rem;
    font-size: 0.8125rem;
    color: #4a5568;

    & h3 {
      font-size: 0.8125rem;
      color: #4a5568;
      margin: 0;
      font-weight: 700;
    }

    & ul {
      margin: 0;
      padding-left: 1.125rem;
    }
  }

  .signature {
    margin: 2.5rem 0 0;
    max-width: 18rem;
    break-inside: avoid;
  }

  /* Kept in the flow so switching the signature on and off moves nothing. */
  .signatureoff {
    visibility: hidden;
  }

  .signaturemark {
    display: block;
    width: 12rem;
    height: auto;
    margin: 0 0 -0.75rem 0.5rem;
    color: #107db5;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .signatureline {
    border-top: 1px solid #1a202c;
    padding-top: 0.5rem;

    & strong {
      display: block;
      color: #0a1530;
    }

    & span {
      display: block;
      font-size: 0.8125rem;
      color: #4a5568;
    }
  }

  .signatureaction {
    margin: 1.5rem 0 0;

    & small {
      display: block;
      margin-top: 0.375rem;
      font-size: 0.8125rem;
      color: #4a5568;
    }
  }

  .disclaimer {
    margin: 2.5rem 0 0;
    padding: 1rem 1.25rem;
    background: rgba(16, 125, 181, 0.06);
    border-radius: 0.25rem;
    font-size: 0.8125rem;
    line-height: 1.6;
    color: #2d3748;
    break-inside: avoid;

    & strong {
      color: #0a1530;
    }
  }

  .bottomactions {
    display: flex;
    justify-content: center;
    margin: 1.5rem 0 0;
  }

  .enterprise {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    gap: 1.5rem;
    margin: 3rem auto 0;
    padding: 2rem;
    border-radius: 0.5rem;
    background: rgba(16, 181, 125, 0.06);

    & h2 {
      margin: 0;
      font-size: 1.25rem;
      line-height: 1;
      color: #0a1530;
    }

    & p {
      margin: 0;
      color: #2d3748;
    }
  }

  .enterpriseicon {
    flex: none;
    width: 7rem;
    height: 7rem;
    color: rgba(16, 181, 125, 0.5);

    @media (max-width: 30rem) {
      display: none;
    }
  }

  .enterprisetext {
    flex: 1 1 20rem;
    display: flex;
    flex-flow: column wrap;
    gap: 0.5rem;
  }

  .enterprisebutton {
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    height: 2.75rem;
    border: 0;
    border-radius: 10px;
    padding: 0 1.25rem;
    font-weight: 600;
    font-size: 1rem;
    color: #fff;
    cursor: pointer;
    transition: 0.25s ease-in-out background;
    background: #67b26f;

    &:hocus {
      background: #107db5;
    }
  }

  .pending {
    color: #4a5568;
    font-weight: 400;
  }

  /* Stands in for the dropdowns when printing, so the printed line reads as a
     sentence instead of inheriting the select's widest-option width. */
  .printonly {
    display: none;
  }
`;

/**
 * The strokes are drawn at slightly different weights, the way a nib varies,
 * and the filter roughens their edges and eats into them with a noise mask so
 * the ink looks laid down on paper rather than vector-drawn.
 */
const SignatureMark = () => (
  <svg
    className={styles.signaturemark}
    viewBox="0 0 340 110"
    role="img"
    aria-label="Signature of Kilian Valkhof"
    xmlns="http://www.w3.org/2000/svg"
  >
    <filter id="quote-ink" x="-15%" y="-15%" width="130%" height="130%" colorInterpolationFilters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="1.2" numOctaves="2" seed="4" result="warp" />
      <feDisplacementMap
        in="SourceGraphic"
        in2="warp"
        scale="1.9"
        xChannelSelector="R"
        yChannelSelector="G"
        result="rough"
      />
      <feTurbulence type="fractalNoise" baseFrequency="0.16 0.07" numOctaves="4" seed="9" result="grain" />
      <feColorMatrix in="grain" type="luminanceToAlpha" result="grainAlpha" />
      <feComponentTransfer in="grainAlpha" result="ink">
        <feFuncA type="linear" slope="1.5" intercept="0.12" />
      </feComponentTransfer>
      <feComposite in="rough" in2="ink" operator="in" />
    </filter>
    <g filter="url(#quote-ink)" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path strokeWidth="4.4" d="M24 84 C30 56 38 32 46 16 C50 8 48 26 44 46 C41 60 40 72 42 84" />
      <path strokeWidth="3.4" d="M76 36 C64 50 52 60 42 64 C52 66 60 74 64 84" />
      <path
        strokeWidth="3.8"
        d="M64 84 C67 70 70 60 73 54 C74 62 73 72 75 82 C79 66 84 40 88 22 C90 12 88 34 85 52 C83 66 84 78 88 82 C92 70 95 60 98 54 C100 64 99 74 101 82 C105 70 108 60 112 56 C116 54 118 60 118 66 C118 72 117 78 120 82 C124 72 128 62 132 56 C136 52 138 58 138 66 C138 74 140 80 146 82"
      />
      <path strokeWidth="4.6" d="M160 30 C166 48 172 64 178 80 C186 62 194 40 202 22" />
      <path
        strokeWidth="3.9"
        d="M212 60 C208 54 202 54 200 60 C198 66 200 76 206 78 C212 80 214 70 216 62 C217 72 219 80 224 82 C228 66 234 40 238 20 C240 10 238 32 235 50 C233 64 234 76 238 80 C242 70 246 62 250 58 C252 68 254 76 258 80 C262 70 266 58 272 52 C276 48 280 52 280 58 C280 66 278 74 282 80 C286 72 290 62 295 58 C300 54 304 58 303 66 C302 78 298 90 293 97 C288 104 282 101 282 95 C282 88 289 82 298 78"
      />
      <path strokeWidth="2.8" d="M270 62 C280 58 292 56 304 56" />
    </g>
  </svg>
);

const PdfIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={styles.buttonicon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
    <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6" />
    <path d="M17 18h2" />
    <path d="M20 15h-3v6" />
    <path d="M11 15v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1" />
  </svg>
);

const CommunityIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={styles.enterpriseicon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M8 9l5 5v7h-5v-4m0 4h-5v-7l5 -5m1 1v-6a1 1 0 0 1 1 -1h10a1 1 0 0 1 1 1v17h-8" />
    <line x1="13" y1="7" x2="13" y2="7.01" />
    <line x1="17" y1="7" x2="17" y2="7.01" />
    <line x1="17" y1="11" x2="17" y2="11.01" />
    <line x1="17" y1="15" x2="17" y2="15.01" />
  </svg>
);

const Page = (props) => {
  const [planKey, setPlanKey] = useState('individual');
  const [yearly, setYearly] = useState(true);
  const [withSignature, setWithSignature] = useState(false);

  const [contactVisible, setContactVisible] = useState(false);

  const [recipient, setRecipient] = useState('');
  const [editingRecipient, setEditingRecipient] = useState(false);
  const [reference, setReference] = useState('');
  const [editingReference, setEditingReference] = useState(false);

  const [prices, setPrices] = useState({});
  const [currency, setCurrency] = useState(false);
  const [meta, setMeta] = useState({ number: '', issued: '' });

  // Generated after mount so the static HTML does not ship a stale date.
  useEffect(() => {
    const now = new Date();
    const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(
      2,
      '0'
    )}`;

    setMeta({
      number: `PP-${stamp}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`,
      issued: formatDate(now),
    });
  }, []);

  // Hide everything that is not the quote while printing. See printStyles.
  useEffect(() => {
    const unmark = () => {
      Array.prototype.forEach.call(document.querySelectorAll(`.${HIDDEN_CLASS}`), (element) => {
        element.classList.remove(HIDDEN_CLASS);
      });
    };

    const mark = () => {
      unmark();

      let node = document.getElementById(QUOTE_ID);

      while (node && node !== document.body) {
        const current = node;
        const parent = current.parentElement;
        if (!parent) break;

        Array.prototype.forEach.call(parent.children, (sibling) => {
          if (sibling !== current) sibling.classList.add(HIDDEN_CLASS);
        });

        node = parent;
      }
    };

    window.addEventListener('beforeprint', mark);
    window.addEventListener('afterprint', unmark);

    return () => {
      window.removeEventListener('beforeprint', mark);
      window.removeEventListener('afterprint', unmark);
      unmark();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchJsonp(`https://checkout.paddle.com/api/2.0/prices?product_ids=${PLANS.individual.ids.yearly}`)
      .then((response) => response.json())
      .then((json) => {
        if (cancelled) return;
        setCurrency(json.response.products[0].currency);
      })
      .catch(() => {});

    const setupPaddle = () => {
      if (cancelled) return;

      if (!window.Paddle) {
        setTimeout(setupPaddle, 100);
        return;
      }

      window.Paddle.Setup({ vendor: PADDLE_VENDOR, debug: false });

      Object.values(PLANS).forEach((plan) => {
        Object.entries(plan.ids).forEach(([billingPeriod, id]) => {
          window.Paddle.Product.Prices(id, (result) => {
            if (cancelled || !result || !result.recurring || !result.recurring.price) return;

            const { gross, net, tax } = result.recurring.price;

            setPrices((previous) => ({
              ...previous,
              [`${plan.key}-${billingPeriod}`]: { gross, net, tax, country: result.country },
            }));
          });
        });
      });
    };

    setupPaddle();

    return () => {
      cancelled = true;
    };
  }, []);

  const plan = PLANS[planKey];
  const period = yearly ? 'yearly' : 'monthly';
  const price = prices[`${plan.key}-${period}`];

  // One unit of the plan for now. See the note on PLANS above.
  const units = 1;

  const net = price ? parseAmount(price.net) * units : NaN;
  const vat = price ? parseAmount(price.tax) * units : NaN;
  const gross = price ? parseAmount(price.gross) * units : NaN;
  const hasVat = !!price && price.net !== price.gross;
  const vatRate = hasVat && net ? Math.round((vat / net) * 1000) / 10 : 0;

  const amount = (value) => formatMoney(value, currency) || <span className={styles.pending}>calculating…</span>;

  const seats = plan.seats * units;
  const currencyName = currency ? CURRENCY_NAMES[currency] || currency : false;

  const hasReference = !!reference.trim();
  const hasRecipient = !!recipient.trim();

  let referenceField;
  if (editingReference) {
    referenceField = (
      <input
        type="text"
        className={styles.referenceinput}
        aria-label="Reference or PO number"
        placeholder="PO-2026-114"
        value={reference}
        ref={focusOnMount}
        onChange={(event) => setReference(event.target.value)}
        onBlur={() => setEditingReference(false)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === 'Escape') setEditingReference(false);
        }}
      />
    );
  } else if (hasReference) {
    referenceField = (
      <button
        type="button"
        className={styles.editable}
        onClick={() => setEditingReference(true)}
        title="Edit reference"
      >
        {reference}
      </button>
    );
  } else {
    referenceField = (
      <button type="button" className={styles.addreference} onClick={() => setEditingReference(true)}>
        + Add reference
      </button>
    );
  }

  let recipientField;
  if (editingRecipient) {
    recipientField = (
      <textarea
        className={styles.recipientinput}
        aria-label="Quote for"
        placeholder={'Your Company\nAddress\nCity, Country\nContact person'}
        value={recipient}
        ref={focusOnMount}
        onChange={(event) => setRecipient(event.target.value)}
        onBlur={() => setEditingRecipient(false)}
      />
    );
  } else if (hasRecipient) {
    recipientField = (
      <button
        type="button"
        className={[styles.editable, styles.recipienttext].join(' ')}
        onClick={() => setEditingRecipient(true)}
        title="Edit recipient"
      >
        {recipient}
      </button>
    );
  } else {
    recipientField = (
      <button type="button" className={styles.addbutton} onClick={() => setEditingRecipient(true)}>
        + Add quote recipient
      </button>
    );
  }

  const print = () => {
    if (typeof window === 'undefined') return;

    if (window.umami) {
      window.umami.track((data) => ({ ...data, url: '/pricing/quote/print/', title: 'Quote printed' }));
    }

    window.print();
  };

  const openContact = () => {
    setContactVisible(true);

    if (typeof window !== 'undefined' && window.umami) {
      window.umami.track((data) => ({ ...data, url: '/pricing/quote-form/', title: 'Request Quote Form' }));
    }
  };

  return (
    <Layout page="pricing">
      <SEO
        title="Create a quote"
        description="Create an indicative quote for a Polypane Individual or Business subscription and save it as a PDF."
        keywords={['']}
        pathname={props.location.pathname}
      />
      <Helmet meta={[{ name: 'robots', content: 'noindex' }]} style={[{ type: 'text/css', cssText: printStyles }]} />

      <PageHeader>
        <h1>Create a quote</h1>
        <h2 className={styles.intro}>
          Need a quote for your finance department? Fill in the quote below, then print the page or save it as a PDF.
        </h2>
      </PageHeader>

      <div className={styles.wrap}>
        <div className={styles.toolbar}>
          <button type="button" className={styles.printbutton} onClick={print}>
            <PdfIcon />
            Print or save as PDF
          </button>
        </div>

        <article id={QUOTE_ID} className={styles.quote}>
          <header className={styles.quoteheader}>
            <img src="/img/brand/polypane-logo.svg" alt="Polypane" className={styles.logo} />
            <div className={styles.quotemeta}>
              <h2>Quote</h2>
              <dl>
                <dt>Quote number</dt>
                <dd>{meta.number || ' '}</dd>
                <dt>Date</dt>
                <dd>{meta.issued || ' '}</dd>
                <div className={[styles.metarow, hasReference ? '' : 'quote-ui'].join(' ')}>
                  <dt>Reference</dt>
                  <dd className={styles.referencevalue}>{referenceField}</dd>
                </div>
              </dl>
            </div>
          </header>

          <div className={[styles.parties, 'quote-parties'].join(' ')}>
            <div>
              <h3>From</h3>
              <p>{['Polypane BV', 'The Netherlands', 'VAT NL859829765B01', 'support@polypane.app'].join('\n')}</p>
            </div>
            <div className={hasRecipient ? '' : 'quote-ui'}>
              <h3>Quote for</h3>
              <div className={[styles.recipientslot, 'quote-recipient'].join(' ')}>{recipientField}</div>
            </div>
          </div>

          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Description</th>
                <th scope="col">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Polypane{' '}
                  <select
                    className={[styles.select, 'quote-ui'].join(' ')}
                    aria-label="Subscription"
                    value={planKey}
                    onChange={(event) => setPlanKey(event.target.value)}
                  >
                    {Object.values(PLANS).map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  <span className={[styles.printonly, 'quote-print-only'].join(' ')}>{plan.name}</span> subscription,
                  billed{' '}
                  <select
                    className={[styles.select, 'quote-ui'].join(' ')}
                    aria-label="Billing period"
                    value={period}
                    onChange={(event) => setYearly(event.target.value === 'yearly')}
                  >
                    <option value="yearly">yearly</option>
                    <option value="monthly">monthly</option>
                  </select>
                  <span className={[styles.printonly, 'quote-print-only'].join(' ')}>{period}</span>
                  <small>
                    {seats} {seats === 1 ? 'seat' : 'seats'}
                  </small>
                </td>
                <td>{amount(net)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Subtotal excl. VAT</th>
                <td>{amount(net)}</td>
              </tr>
              <tr>
                <th scope="row">VAT{hasVat && vatRate ? ` (${vatRate}%)` : ''}</th>
                <td>{price ? formatMoney(hasVat ? vat : 0, currency) : amount(NaN)}</td>
              </tr>
              <tr>
                <th scope="row">Total per {yearly ? 'year' : 'month'}</th>
                <td>{amount(gross)}</td>
              </tr>
            </tfoot>
          </table>

          <div className={[styles.terms, 'quote-terms'].join(' ')}>
            <h3>Terms</h3>
            <ul>
              <li>
                {currencyName ? `All amounts are in ${currencyName}. ` : ''}
                VAT is shown at the rate for your location and is recalculated at checkout based on your billing country
                and VAT number.
              </li>
              <li>
                The subscription starts on the day of purchase and is automatically renewed at the end of every billing
                period, unless it is cancelled before the renewal date.
              </li>
              <li>
                Polypane subscriptions are securely sold and invoiced by Paddle.com, our merchant of record. Payment is
                by credit card or PayPal, as well as Apple Pay and Google Pay where available.
              </li>
            </ul>
          </div>

          <p className={[styles.disclaimer, 'quote-disclaimer'].join(' ')}>
            <strong>This quote is indicative.</strong> It is generated automatically for budgeting and approval purposes
            and is not a binding offer. Prices are subject to change and no rights can be derived from this document.
            The prices published on polypane.app at the time of purchase are the ones that apply.
          </p>

          <p className={[styles.signatureaction, 'quote-ui'].join(' ')}>
            <button
              type="button"
              className={styles.addbutton}
              onClick={() => setWithSignature((previous) => !previous)}
              aria-pressed={withSignature}
            >
              {withSignature ? 'Remove signature' : 'Sign the quote on behalf of Polypane, by Kilian Valkhof, CEO.'}
            </button>
          </p>

          <div
            className={[
              styles.signature,
              'quote-signature',
              withSignature ? '' : styles.signatureoff,
              withSignature ? '' : 'quote-signature-off',
            ].join(' ')}
            aria-hidden={!withSignature}
          >
            <SignatureMark />
            <div className={styles.signatureline}>
              <strong>Kilian Valkhof</strong>
              <span>Polypane CEO</span>
            </div>
          </div>
        </article>

        <div className={styles.bottomactions}>
          <button type="button" className={styles.printbutton} onClick={print}>
            <PdfIcon />
            Print or save as PDF
          </button>
        </div>

        <aside className={styles.enterprise}>
          <CommunityIcon />
          <div className={styles.enterprisetext}>
            <h2>Need something more specific?</h2>
            <p>
              Larger teams with needs around user management, custom billing or supplier portal registration are a
              better fit for our Enterprise plan.
            </p>
            <button type="button" className={styles.enterprisebutton} onClick={openContact}>
              Talk to us about Enterprise
            </button>
          </div>
        </aside>

        <RequestQuote visible={contactVisible} setVisible={setContactVisible} />
      </div>

      <div style={{ height: '4rem' }} />
    </Layout>
  );
};

export default Page;
